const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}

/* ============================================================
   AWC .fade-up reveal + Rosedale page-content cleanup (2026-06-30)
   Why: the Rosedale page body (page.content) ships .fade-up CSS
   (opacity:0 -> .in-view opacity:1) plus a comment for an
   IntersectionObserver, but Shopify's page editor STRIPS <script>
   from page bodies, so the observer never ran and every section
   stayed opacity:0 (the page looked empty). This restores reveal
   at theme level (theme.liquid loads animations.js site-wide) and
   cleans two cosmetic artifacts left behind. Scoped so it is inert
   on pages that do not use these patterns.
   ============================================================ */
(function () {
  // AWC custom pages/sections use several scroll-reveal conventions whose base
  // class differs per page (.fade-up, .reveal, .sc-fade-in, [data-stagger], ...)
  // but the marker class that flips them visible is consistently "is-visible" or
  // "in-view". Many pages lost their reveal trigger (Shopify strips <script> from
  // page bodies, and a number of section observers also fail to run with no JS
  // error), so that content stays stuck at opacity:0 and the page looks blank.
  //
  // We don't read the stylesheets (animation-based reveals and cross-origin
  // sheets make that unreliable). Instead we select elements that use a known
  // reveal base class/attribute and, once they scroll into view, add BOTH marker
  // classes. The page's own CSS then runs its intended transition/animation
  // (so the fade-in is preserved). Adding a marker that matches no rule is a
  // no-op, and re-adding one an existing observer already set is idempotent, so
  // this is safe on pages whose own observer still works.
  var REVEAL_SELECTOR = '[class*="fade"], [class*="reveal"], [data-stagger]';
  var MARKERS = ['is-visible', 'in-view'];

  function markVisible(el) {
    for (var i = 0; i < MARKERS.length; i++) el.classList.add(MARKERS[i]);
  }

  function revealAll() {
    var els = document.querySelectorAll(REVEAL_SELECTOR);
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, markVisible);
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          markVisible(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px 0px' });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  function cleanupRosedale() {
    var main = document.querySelector('.awc-chiro-rosedale');
    if (!main) return; // Rosedale only

    // 1) Remove empty <p> artifacts left around the main element
    //    (leftover from the stripped script block) that create a large
    //    blank band above the hero. Scoped to the page-content wrapper.
    var wrap = main.parentElement;
    if (wrap) {
      wrap.querySelectorAll(':scope > p').forEach(function (p) {
        if (!p.textContent.trim() && !p.querySelector('img,svg,iframe,video,input')) {
          p.remove();
        }
      });
    }

    // 2) Fix UTF-8 mojibake: a stray U+00C2 character sits before the
    //    middot separator in the "Last updated / Medically reviewed" byline.
    var moji = String.fromCharCode(0xC2); // stray "Â" left by a UTF-8/Latin-1 mis-decode
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.indexOf(moji) !== -1) {
        node.nodeValue = node.nodeValue.split(moji).join('');
      }
    }
  }

  function run() {
    revealAll();
    cleanupRosedale();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
